import { ChevronDown, ChevronRight, Info, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PremiumBadge } from '~/components/common/premium-badge';
import { Card, CardContent, CardHeader } from '~/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import type { BatchProcessingColumn } from '~/features/catalog-operations/types/catalog-operations.types';

interface Props {
  columns: BatchProcessingColumn[];
  /** Called when the user clicks a drill-in row. Receives the row label. */
  onDrillIn?: (label: string) => void;
}

export function BatchProcessingDetails({ columns, onDrillIn }: Props) {
  const { t } = useTranslation('catalog-operations');
  const maxRows = Math.max(...columns.map((col) => col.rows.length));

  return (
    <Card data-testid="batch-detail-processing-details-card">
      <CardHeader className="flex flex-row items-center gap-4 p-6">
        <div className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          <Layers className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="space-y-0.5">
          <h2 data-testid="batch-detail-processing-details-title" className="text-xl">
            {t('batchProcessingDetails.title')}
          </h2>
          <p className="text-muted-foreground text-sm font-light">
            {t('batchProcessingDetails.subtitle')}
          </p>
        </div>
      </CardHeader>

      <CardContent className="bg-neutral-10 mr-6 mb-6 ml-6 rounded-xl px-0 py-3">
        <div className="grid grid-cols-3">
          {columns.map((col) => (
            <div key={col.title} className="px-6 py-4 not-last:border-r">
              <p className="text-sm font-semibold">{col.title}</p>
            </div>
          ))}
        </div>

        {Array.from({ length: maxRows }).map((_, rowIdx) => (
          <div key={rowIdx} className="grid grid-cols-3">
            {columns.map((col) => {
              const row = col.rows[rowIdx];
              return (
                <div key={col.title} className="px-6 not-last:border-r">
                  <div
                    data-testid={row ? `batch-detail-processing-item-${row.label}` : undefined}
                    className="border-t py-3"
                  >
                    {row && (
                      <>
                        <div className="text-muted-foreground flex items-center gap-1 text-xs">
                          <span>{row.label}</span>
                          {row.info && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  data-testid={`batch-detail-processing-info-button-${row.label}`}
                                  aria-label={t('batchProcessingDetails.infoLabel', {
                                    label: row.label,
                                  })}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>{row.label}</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        {row.drillIn && onDrillIn && !row.disabled ? (
                          <button
                            type="button"
                            data-testid={`batch-detail-processing-drill-in-button-${row.label}`}
                            className="group mt-1 flex cursor-pointer items-center gap-1 hover:text-[#445ce4]"
                            onClick={() => {
                              onDrillIn(row.label);
                            }}
                          >
                            <p
                              data-testid={`batch-detail-processing-value-${row.label}`}
                              className="text-base font-semibold tabular-nums"
                            >
                              {row.value}
                            </p>
                            {row.expandable ? (
                              <ChevronDown
                                className="text-muted-foreground h-4 w-4 group-hover:text-[#445ce4]"
                                aria-hidden="true"
                              />
                            ) : (
                              <ChevronRight
                                className="text-muted-foreground h-4 w-4 group-hover:text-[#445ce4]"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        ) : (
                          <div
                            className={`mt-1 flex items-center gap-1 ${
                              row.drillIn && row.disabled ? 'cursor-not-allowed' : ''
                            }`}
                          >
                            <p
                              data-testid={`batch-detail-processing-value-${row.label}`}
                              className="text-base font-semibold tabular-nums"
                            >
                              {row.value}
                            </p>
                            {row.locked &&
                              (row.lockedTooltip ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex cursor-help">
                                      <PremiumBadge size={14} />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    {row.lockedTooltip}
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <PremiumBadge size={14} />
                              ))}
                            {row.expandable && (
                              <ChevronDown
                                className="text-muted-foreground h-4 w-4"
                                aria-hidden="true"
                              />
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
